// Edo-period fishing lantern: a woven bamboo basket around an oil cup and
// wick, burning fish oil. It hangs from the boat's crossbar when he's
// afloat and swings at his side on foot, and while lit it pushes back the
// night's view distance (see weather.ts's lanternLight).
//
// Fuel is the point of it: oil burns down whenever the flame is up, so
// night fishing costs something and running dry in open water is a real
// event rather than a warning.

import * as THREE from 'three';
import { PROP_SCALE } from '@world/terrain';
import { toonMat, addOutlines } from '@world/toon';
import { DEV_START_WITH_LANTERN } from './dev';

export const LANTERN_PRICE = 45;   // coins, at a village market
export const OIL_PRICE = 9;        // coins per flask
export const OIL_FLASK = 40;       // fuel units per flask
export const OIL_MAX = 120;        // the lantern's own capacity
const BURN_PER_SECOND = 0.32;      // fuel per second of game time, lit

export const lantern = {
  owned: DEV_START_WITH_LANTERN,   // normally bought at a village market
  lit: false,
  oil: DEV_START_WITH_LANTERN ? OIL_MAX : 0,
};

// ================= Mesh =================
// Built to the reference: a bellied woven basket, a dark collar at the
// shoulder and a foot ring at the base, an arched carrying hoop, and a
// flame inside that shows through the weave.
const bambooMat = toonMat(0x9a7f56);
const bandMat = toonMat(0x4a3a2a);
const flameMat = toonMat(0xffd489, 0xd88a2a);

export const lanternGroup = new THREE.Group();
let flameMesh: THREE.Mesh;
const lanternLight = new THREE.PointLight(0xffb765, 0, 3.2, 2);

function buildLanternMesh(): THREE.Group {
  const g = new THREE.Group();
  // Belly: a lathe profile, wider at the shoulder than at either end, so it
  // reads as the woven basket rather than a plain cylinder.
  const profile = [
    new THREE.Vector2(0.10, 0.00),
    new THREE.Vector2(0.15, 0.06),
    new THREE.Vector2(0.17, 0.17),
    new THREE.Vector2(0.155, 0.28),
    new THREE.Vector2(0.11, 0.36),
  ];
  const body = new THREE.Mesh(new THREE.LatheGeometry(profile, 9), bambooMat);
  g.add(body);
  // Collar and foot ring
  ([[0.355, 0.115], [0.01, 0.108]] as const).forEach(([y, r]) => {
    const ring = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 0.035, 9), bandMat);
    ring.position.y = y;
    g.add(ring);
  });
  // Horizontal weave bands around the belly
  ([0.09, 0.19, 0.29] as const).forEach((y) => {
    const w = new THREE.Mesh(new THREE.TorusGeometry(0.163, 0.008, 4, 10), bandMat);
    w.rotation.x = Math.PI / 2;
    w.position.y = y;
    g.add(w);
  });
  // Carrying hoop over the top
  const hoop = new THREE.Mesh(new THREE.TorusGeometry(0.115, 0.012, 4, 12, Math.PI), bandMat);
  hoop.position.y = 0.37;
  g.add(hoop);
  // Oil cup and flame, sitting low inside the basket
  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.04, 8), bandMat);
  cup.position.y = 0.06;
  g.add(cup);
  flameMesh = new THREE.Mesh(new THREE.ConeGeometry(0.032, 0.11, 6), flameMat);
  flameMesh.position.y = 0.13;
  g.add(flameMesh);

  addOutlines(g);
  // The flame is light, not an object — an ink line around it reads wrong.
  flameMesh.children.forEach((o) => { o.visible = false; });
  // Half again below prop scale: a hand lantern is a small thing next to
  // him, and at full PROP_SCALE it read as a barrel he was carrying.
  g.scale.setScalar(PROP_SCALE * 0.5);
  return g;
}

lanternGroup.add(buildLanternMesh());
lanternGroup.add(lanternLight);
lanternGroup.visible = false;

// ================= State =================
export function giveLantern(withOil = OIL_MAX): void {
  lantern.owned = true;
  lantern.oil = Math.min(OIL_MAX, withOil);
}

export function refillLantern(amount: number): number {
  const before = lantern.oil;
  lantern.oil = Math.min(OIL_MAX, lantern.oil + amount);
  return lantern.oil - before;
}

// Returns the new lit state, or null if he can't light it at all.
export function toggleLantern(): boolean | null {
  if (!lantern.owned) return null;
  if (!lantern.lit && lantern.oil <= 0) return null;
  lantern.lit = !lantern.lit;
  return lantern.lit;
}

// 0..1, how much light it's throwing. Fades out over the last of the oil so
// the flame gutters rather than snapping off.
export function lanternGlow(): number {
  if (!lantern.owned || !lantern.lit || lantern.oil <= 0) return 0;
  return Math.min(1, lantern.oil / 12);
}

// dt is game seconds. Returns true on the tick the flame actually dies, so
// the caller can toast it.
export function updateLantern(dt: number, x: number, y: number, z: number, now: number): boolean {
  let justDied = false;
  if (lantern.lit) {
    lantern.oil = Math.max(0, lantern.oil - BURN_PER_SECOND * dt);
    if (lantern.oil <= 0) { lantern.lit = false; justDied = true; }
  }
  const glow = lanternGlow();
  lanternGroup.visible = lantern.owned && (lantern.lit || glow > 0);
  if (!lanternGroup.visible) {
    lanternLight.intensity = 0;
    return justDied;
  }
  lanternGroup.position.set(x, y, z);
  // Swing, and a flicker on the flame and the light together
  const flicker = 0.82 + Math.sin(now * 0.011) * 0.09 + Math.sin(now * 0.037) * 0.06;
  lanternGroup.rotation.z = Math.sin(now * 0.0022) * 0.12;
  lanternLight.intensity = 1.9 * glow * flicker;
  lanternLight.position.y = 0.13 * PROP_SCALE * 0.5; // sits at the wick, which halved with the mesh
  if (flameMesh) flameMesh.scale.setScalar(0.85 + flicker * 0.2);
  return justDied;
}
